import { composeWrappers } from "@redotech/react-util/component";
import { HidProvider } from "@redotech/react-util/hid";
import { NotificationProvider } from "@redotech/redo-web/notification-provider";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import { CustomSnackbarProvider } from "@redotech/redo-web/snackbar";
import { ThemeProvider } from "@redotech/redo-web/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactNode, useContext, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AmplitudeProvider } from "./core/amplitude-provider";
import { getMerchantServerUrl } from "./core/auth";
import { AuthProvider, useAuth } from "./core/auth-provider";
import { prefetchMerchantInfo } from "./hooks/use-merchant-info";
import { useIdentifyMerchant } from "./hooks/use-onboarding-tracking";
import { routes } from "./route";
import {
  OnboardingV2RpcClientContext,
  OnboardingV2RpcClientProvider,
} from "./rpc-client";
import "./styles.css";

const router = createBrowserRouter(routes);

function RpcProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  return (
    <OnboardingV2RpcClientProvider
      authToken={auth?.token}
      baseUrl={getMerchantServerUrl()}
    >
      <PrefetchMerchantInfo />
      {children}
    </OnboardingV2RpcClientProvider>
  );
}

function PrefetchMerchantInfo() {
  const rpcClient = useContext(OnboardingV2RpcClientContext);
  useEffect(() => {
    prefetchMerchantInfo(rpcClient);
  }, [rpcClient]);
  return null;
}

function MerchantIdentifier() {
  useIdentifyMerchant();
  return null;
}

const OnboardingApp = () => {
  return composeWrappers(
    (inner) => <AuthProvider>{inner}</AuthProvider>,
    (inner) => <AmplitudeProvider>{inner}</AmplitudeProvider>,
    (inner) => <RpcProvider>{inner}</RpcProvider>,
    (inner) => (
      <QueryClientProvider client={reactQueryClient}>
        {inner}
      </QueryClientProvider>
    ),
    (inner) => <HidProvider>{inner}</HidProvider>,
    (inner) => <ThemeProvider>{inner}</ThemeProvider>,
    (inner) => <NotificationProvider>{inner}</NotificationProvider>,
    (inner) => <CustomSnackbarProvider>{inner}</CustomSnackbarProvider>,
  )(
    <>
      <MerchantIdentifier />
      <RouterProvider router={router} />
    </>,
  );
};

const root = createRoot(document.getElementById("root")!);
root.render(<OnboardingApp />);
